import type { PhaseCatalogDto, SiteProgressDto } from '@app/contracts';

interface Props {
  catalog: PhaseCatalogDto[];
  progress: SiteProgressDto;
  onSelect: (phaseCode: string) => void;
  selectedPhaseCode: string;
}

export function PhaseTimeline({ catalog, progress, onSelect, selectedPhaseCode }: Props) {
  const sitePhase = catalog.find((p) => p.code === progress.sitePhaseCode);
  const userPhase = catalog.find((p) => p.code === progress.userPhaseCode);
  const sitePhaseOrder = sitePhase?.order ?? 0;
  const userPhaseOrder = userPhase?.order ?? 0;

  return (
    <ol className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
      {catalog
        .filter((p) => p.order > 0)
        .map((p) => {
          const reachedSite = p.order <= sitePhaseOrder;
          const reachedUser = p.order <= userPhaseOrder;
          const phaseProgress = progress.perPhase.find((pp) => pp.phaseCode === p.code);
          const isSelected = p.code === selectedPhaseCode;
          const status: 'locked' | 'available' | 'in-progress' | 'done' = !reachedSite
            ? 'locked'
            : reachedUser && p.order < userPhaseOrder
              ? 'done'
              : reachedUser && p.order === userPhaseOrder
                ? 'in-progress'
                : 'available';

          const isLocked = p.order > userPhaseOrder;

          return (
            <li key={p.code}>
              <button
                type="button"
                onClick={() => onSelect(p.code)}
                disabled={isLocked}
                className={`flex w-full flex-col gap-2 rounded-2xl border p-4 text-left transition ${
                  isSelected
                    ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-500/30'
                    : 'border-slate-200 bg-white/80 hover:border-emerald-300'
                } ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-emerald-800/95">
                    Phase {p.order}
                  </span>
                  <PhaseStatusBadge status={status} />
                </div>
                <p className="text-sm font-semibold leading-snug tracking-tight text-slate-900">
                  {p.label.replace(/^Phase\s+\d+\s*:\s*/i, '')}
                </p>
                {phaseProgress && phaseProgress.mandatoryTotal > 0 && (
                  <div className="mt-1">
                    <div className="h-1.5 overflow-hidden rounded-full bg-slate-200">
                      <div
                        className="h-full rounded-full bg-emerald-500"
                        style={{ width: `${phaseProgress.progressPercent}%` }}
                      />
                    </div>
                    <p className="mt-1 text-[0.7rem] text-slate-500">
                      {phaseProgress.mandatoryDone}/{phaseProgress.mandatoryTotal} obligatoires
                    </p>
                  </div>
                )}
              </button>
            </li>
          );
        })}
    </ol>
  );
}

function PhaseStatusBadge({
  status,
}: {
  status: 'locked' | 'available' | 'in-progress' | 'done';
}) {
  const cls = {
    locked: 'bg-slate-100 text-slate-500',
    available: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-500/30',
    'in-progress': 'bg-amber-50 text-amber-700 ring-1 ring-amber-500/30',
    done: 'bg-emerald-600 text-white',
  }[status];
  const label = {
    locked: 'Verrouillée',
    available: 'À venir',
    'in-progress': 'En cours',
    done: 'Validée',
  }[status];
  return (
    <span className={`rounded-full px-2 py-0.5 text-[0.65rem] font-semibold uppercase ${cls}`}>
      {label}
    </span>
  );
}
